import { useEffect, useState } from "react";
import { Plus, Trash, Sparkle, CheckCircle, X, ChatCircleDots, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

// "Mimi's Training" — operator adds Q&A pairs that Mimi will use verbatim.
// Layer 2 of the training model. Deterministic, plain English, auditable.

const STARTER_EXAMPLES = [
  { q: "Can I bring my own cake?", a: "Yes — $2/person plating fee. Just let your server know when you arrive." },
  { q: "Do you validate parking?", a: "We don't validate, but the garage on Fillmore is $4/hr after 6pm." },
  { q: "Is there a corkage fee?", a: "$10/bottle on Sundays. Other nights we don't allow outside wine." },
];

export default function DashMimiTraining() {
  const [pairs, setPairs] = useState([]);
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // Training preview — lets the operator prove a pair works BEFORE calling.
  const [previewQ, setPreviewQ] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const runPreview = async () => {
    if (!previewQ.trim()) return toast.error("Type a hypothetical caller question first.");
    setPreviewBusy(true);
    setPreviewResult(null);
    try {
      const r = await api.post("/mimi/training/preview", { question: previewQ.trim() });
      setPreviewResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed.");
    }
    setPreviewBusy(false);
  };

  const load = () => api.get("/mimi/training").then((r) => setPairs(r.data.pairs || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e?.preventDefault?.();
    if (q.trim().length < 3 || a.trim().length < 1) {
      toast.error("Question and answer are both required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/mimi/training", { question: q.trim(), answer: a.trim(), active: true });
      toast.success("Mimi learned it.");
      setQ(""); setA(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save.");
    }
    setBusy(false);
  };

  const applyStarter = (s) => { setQ(s.q); setA(s.a); };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/mimi/training/${p.id}`, { active: !p.active });
      load();
    } catch (e) { toast.error("Couldn't update."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this training pair?")) return;
    try {
      await api.delete(`/mimi/training/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) { toast.error("Couldn't delete."); }
  };

  return (
    <div className="max-w-4xl">
      <div className="overline">Layer 2 — Custom Q&amp;A</div>
      <h1 className="text-3xl mb-2">Mimi's Training</h1>
      <p className="text-sm text-[#5C6B61] mb-6 leading-snug max-w-2xl">
        Teach Mimi how to answer edge-case questions specific to your restaurant. She'll use these answers <b>verbatim</b> when a caller asks something similar. Up to 60 pairs. Plain English. Deterministic — no ML magic, no drift.
      </p>

      <form onSubmit={add} className="paper-card p-5 mb-6" data-testid="training-add-form">
        <div className="overline mb-3 flex items-center gap-2"><Sparkle size={12} weight="fill" color="#D96C5B" /> Teach Mimi a new answer</div>
        <div className="space-y-3">
          <div>
            <label className="overline block mb-1">Question a caller might ask</label>
            <input
              className="input-field"
              placeholder="Can I bring my own cake?"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              maxLength={300}
              data-testid="training-question"
            />
          </div>
          <div>
            <label className="overline block mb-1">Mimi's answer (she says this verbatim)</label>
            <textarea
              className="input-field"
              rows={3}
              placeholder="Yes — $2/person plating fee. Just let your server know when you arrive."
              value={a}
              onChange={(e) => setA(e.target.value)}
              maxLength={1000}
              data-testid="training-answer"
            />
            <div className="text-[10px] font-mono text-[#5C6B61] mt-1">{a.length}/1000</div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-[#5C6B61]">Try one:</span>
              {STARTER_EXAMPLES.map((s) => (
                <button key={s.q} type="button" onClick={() => applyStarter(s)} className="text-[11px] px-2 py-1 rounded-full bg-[#E6E2D6] hover:bg-[#C2D1C1] transition-colors" data-testid={`training-starter-${s.q.replace(/\W+/g, '-').toLowerCase()}`}>
                  {s.q}
                </button>
              ))}
            </div>
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 bg-[#0E1512] text-white text-sm font-bold px-4 py-2 rounded-full hover:bg-[#1E3A2F] transition-colors disabled:opacity-60"
              data-testid="training-submit"
            >
              <Plus size={14} weight="bold" /> {busy ? "Saving…" : "Add"}
            </button>
          </div>
        </div>
      </form>

      <div className="mb-3 flex items-center justify-between">
        <div className="overline">Mimi has learned {pairs.length} answer{pairs.length === 1 ? "" : "s"}</div>
        <div className="text-[10px] font-mono text-[#5C6B61]">Cap: 60 pairs</div>
      </div>

      {loading ? (
        <div className="text-sm text-[#5C6B61]">Loading…</div>
      ) : pairs.length === 0 ? (
        <div className="paper-card p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-[#F5C543]/25 flex items-center justify-center mx-auto mb-3">
            <Sparkle size={22} color="#D96C5B" weight="fill" />
          </div>
          <div className="font-heading font-extrabold text-lg mb-1">Mimi's still learning your restaurant</div>
          <p className="text-sm text-[#5C6B61] max-w-md mx-auto leading-snug">
            Add your first Q&amp;A above. Every question you teach her is one less transfer to a person during dinner rush.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="training-list">
          {pairs.map((p) => (
            <div key={p.id} className={`paper-card p-4 border-l-4 ${p.active ? "border-[#1E3A2F]" : "border-[#5C6B61] opacity-60"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] tracking-widest uppercase text-[#5C6B61] font-bold mb-1">Question</div>
                  <div className="text-sm text-[#121914] font-semibold mb-2">{p.question}</div>
                  <div className="text-[11px] tracking-widest uppercase text-[#D96C5B] font-bold mb-1">Mimi answers</div>
                  <div className="text-sm text-[#121914] leading-snug italic">"{p.answer}"</div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button
                    onClick={() => toggleActive(p)}
                    className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${p.active ? "bg-[#C2D1C1] text-[#1E3A2F]" : "bg-[#E6E2D6] text-[#5C6B61]"}`}
                    data-testid={`training-toggle-${p.id}`}
                  >
                    {p.active ? <><CheckCircle size={10} weight="fill" className="inline" /> Active</> : <><X size={10} weight="bold" className="inline" /> Paused</>}
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="text-[10px] text-[#A5382A] hover:underline inline-flex items-center gap-1"
                    data-testid={`training-delete-${p.id}`}
                  >
                    <Trash size={11} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ Training Preview ═══
          Simulates what Mimi would say to a hypothetical caller question using
          the exact same prompt logic + LLM as the real voice pipeline. Zero
          Twilio cost. Zero real-call risk. If a pair doesn't catch a phrasing
          here, it won't catch it on a real call either — fix it before it hurts. */}
      <div className="mt-8 bg-[#0E1512] text-white rounded-2xl p-5 md:p-6" data-testid="training-preview-block">
        <div className="flex items-center gap-2 mb-2">
          <ChatCircleDots size={20} weight="fill" color="#F5C543" />
          <div className="overline text-[#F5C543]">Test Mimi</div>
        </div>
        <h3 className="font-heading text-xl font-extrabold mb-1">Ask Mimi something a caller might say.</h3>
        <p className="text-xs text-white/60 mb-4">Type a hypothetical question. We'll run it through the same prompt Mimi uses on real calls — no Twilio charge — and show you exactly what she'd answer or whether she'd transfer.</p>
        <div className="flex gap-2">
          <input
            className="input-field !bg-white/10 !text-white !border-white/20 flex-1 !placeholder-white/40"
            placeholder='e.g. "do you have parking?" or "what time do you close?"'
            value={previewQ}
            onChange={(e) => setPreviewQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runPreview()}
            data-testid="training-preview-input"
          />
          <button
            onClick={runPreview}
            disabled={previewBusy || !previewQ.trim()}
            className="inline-flex items-center gap-1 bg-[#F5C543] text-[#0E1512] font-bold px-4 py-2 rounded-full hover:bg-[#F5C543]/90 disabled:opacity-50 text-sm"
            data-testid="training-preview-run"
          >
            {previewBusy ? "Asking…" : "Ask Mimi"} <ArrowRight size={14} weight="bold" />
          </button>
        </div>
        {previewResult && (
          <div className="mt-4 space-y-2" data-testid="training-preview-result">
            {previewResult.no_training ? (
              <div className="bg-white/10 rounded-lg p-3 text-sm text-white/80">{previewResult.answer}</div>
            ) : previewResult.would_transfer ? (
              <>
                <div className="bg-[#A5382A]/20 border border-[#A5382A]/40 rounded-lg p-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#F5C543] mb-1">Would transfer to a person</div>
                  <div className="text-sm text-white/80">None of your training pairs matched this question. Add a pair for it above, or tweak an existing pair's Q so it matches this phrasing.</div>
                </div>
                <div className="text-xs text-white/60">Mimi's response: <span className="italic">"{previewResult.answer}"</span></div>
              </>
            ) : (
              <>
                <div className="bg-[#1E3A2F]/40 border border-[#1E3A2F]/60 rounded-lg p-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#F5C543] mb-1 flex items-center gap-1">
                    <CheckCircle size={11} weight="fill" /> Trained pair matched
                  </div>
                  <div className="text-sm text-white/80 mb-2 italic">Matched Q: "{previewResult.matched_pair}"</div>
                  <div className="text-sm font-semibold text-white">Mimi says: "{previewResult.answer}"</div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
